import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  BookOpen, 
  ArrowLeft, 
  Calculator, 
  ChevronRight, 
  Clock, 
  User, 
  Search,
  BookMarked,
  Award,
  TrendingUp,
  Sliders,
  DollarSign,
  Activity,
  ArrowUpRight,
  Sparkles
} from "lucide-react";
import SEO from "../seo/SEO";
import { blogArticles, BlogArticle } from "../data/blogData";

export default function FinancialGuides() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Filtered list by Category AND real-time query match
  const filteredGuides = useMemo(() => {
    let result = blogArticles;
    
    if (activeCategory !== "all") {
      result = result.filter(g => g.category === activeCategory);
    }

    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      result = result.filter(g => 
        g.title.toLowerCase().includes(q) || 
        g.summary.toLowerCase().includes(q) ||
        g.sections.some(s => s.heading.toLowerCase().includes(q) || s.body.toLowerCase().includes(q))
      );
    }

    return result;
  }, [activeCategory, searchQuery]);

  // Designate the first article as the primary Featured Guide on load
  const featuredGuide = useMemo(() => {
    return blogArticles.find(a => a.id === "calculate-net-income-from-balance-sheet") || blogArticles[0];
  }, []);

  // Filter out the featured guide from the standard card grid to preserve visual rhythm
  const gridGuides = useMemo(() => {
    return filteredGuides.filter(g => g.id !== featuredGuide.id || searchQuery.trim() !== "");
  }, [filteredGuides, featuredGuide, searchQuery]);

  const categoriesCount = useMemo(() => {
    return {
      all: blogArticles.length,
      mortgage: blogArticles.filter(g => g.category === "mortgage").length,
      savings: blogArticles.filter(g => g.category === "savings").length,
      debt: blogArticles.filter(g => g.category === "debt").length,
      retirement: blogArticles.filter(g => g.category === "retirement").length,
    };
  }, []);

  return (
    <div className="space-y-12 py-4">
      <SEO 
        title="Financial Guides, Insights &amp; Calculators | USMoneyAI" 
        description="Expand your personal financial literacy with clear, mathematically validated guides on mortgage amortization, compound interest growth, debt payoff strategies, and retirement planning. Read our expert publications."
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Blog", url: "/blog" }
        ]}
      />

      {/* Top Breadcrumb & Return Action */}
      <div className="flex items-center justify-between border-b border-slate-900 pb-5 text-left">
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-xs font-mono font-semibold text-slate-400 hover:text-amber-500 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Calculator Dashboard</span>
        </Link>
        <span className="text-[10px] font-mono tracking-wider text-slate-500 uppercase">
          USMoneyAI Editorial Library
        </span>
      </div>

      {/* Hero Header Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 text-left items-end">
        <div className="lg:col-span-8 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-xs text-amber-500 font-mono">
            <BookMarked className="w-3.5 h-3.5 text-amber-500" />
            <span>USMoneyAI Editorial Library</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-white tracking-tight leading-[1.12]">
            Knowledge Base for Smarter Money Decisions
          </h1>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed font-light max-w-2.5xl">
            We translate complex amortization rules, investment equations, card percentages, and safety calculations into clear guides. Each guide integrates directly with our accurate calculators so you can act immediately.
          </p>
        </div>

        {/* High Conversion Search Bar */}
        <div className="lg:col-span-4 w-full">
          <div className="relative">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-slate-500">
              <Search className="w-4.5 h-4.5" />
            </div>
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search guides, strategies..."
              className="w-full bg-slate-950/50 border border-slate-900 focus:border-amber-500/40 focus:outline-none rounded-xl py-3 pl-11 pr-4 text-xs sm:text-sm text-white placeholder-slate-500 transition-all font-light"
            />
          </div>
        </div>
      </div>

      {/* Category Tabs Section */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-900/60 pb-5 text-left">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCategory("all")}
            className={`px-4.5 py-2 rounded-xl text-xs font-semibold border transition-all duration-200 cursor-pointer ${
              activeCategory === "all"
                ? "bg-amber-500 text-slate-950 border-amber-400 font-extrabold"
                : "bg-slate-950/40 text-slate-400 border-slate-900 hover:text-white"
            }`}
          >
            All Guides ({categoriesCount.all})
          </button>
          <button
            onClick={() => setActiveCategory("mortgage")}
            className={`px-4.5 py-2 rounded-xl text-xs font-semibold border transition-all duration-200 cursor-pointer ${
              activeCategory === "mortgage"
                ? "bg-amber-500 text-slate-950 border-amber-400 font-extrabold"
                : "bg-slate-950/40 text-slate-400 border-slate-900 hover:text-white"
            }`}
          >
            Mortgages ({categoriesCount.mortgage})
          </button>
          <button
            onClick={() => setActiveCategory("savings")}
            className={`px-4.5 py-2 rounded-xl text-xs font-semibold border transition-all duration-200 cursor-pointer ${
              activeCategory === "savings"
                ? "bg-amber-500 text-slate-950 border-amber-400 font-extrabold"
                : "bg-slate-950/40 text-slate-400 border-slate-900 hover:text-white"
            }`}
          >
            Wealth &amp; Savings ({categoriesCount.savings})
          </button>
          <button
            onClick={() => setActiveCategory("debt")}
            className={`px-4.5 py-2 rounded-xl text-xs font-semibold border transition-all duration-200 cursor-pointer ${
              activeCategory === "debt"
                ? "bg-amber-500 text-slate-950 border-amber-400 font-extrabold"
                : "bg-slate-950/40 text-slate-400 border-slate-900 hover:text-white"
            }`}
          >
            Debt Management ({categoriesCount.debt})
          </button>
          <button
            onClick={() => setActiveCategory("retirement")}
            className={`px-4.5 py-2 rounded-xl text-xs font-semibold border transition-all duration-200 cursor-pointer ${
              activeCategory === "retirement"
                ? "bg-amber-500 text-slate-950 border-amber-400 font-extrabold"
                : "bg-slate-950/40 text-slate-400 border-slate-900 hover:text-white"
            }`}
          >
            Retirement ({categoriesCount.retirement})
          </button>
        </div>
      </div>

      {/* Featured Editorial Guide: Only visible when categories is 'all' or empty search */}
      {activeCategory === "all" && searchQuery.trim() === "" && (
        <article className="bg-gradient-to-tr from-slate-950 via-[#0a0f1d] to-slate-950 border border-slate-900 hover:border-slate-805 rounded-2xl p-6 sm:p-8 md:p-10 text-left transition-all relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-48 h-48 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.03),transparent_40%)]" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-8 space-y-5">
              <div className="flex items-center gap-3 text-xs font-mono font-semibold text-amber-500">
                <span className="bg-amber-500/10 border border-amber-500/20 px-2.5 py-0.5 rounded-full uppercase tracking-wider text-[10px]">
                  FEATURED PUBLICATION
                </span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {featuredGuide.readTime}
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-white tracking-tight leading-tight group-hover:text-amber-400 transition-colors">
                <Link to={`/blog/${featuredGuide.slug}`}>
                  {featuredGuide.title}
                </Link>
              </h2>

              <p className="text-slate-400 text-sm sm:text-base font-light leading-relaxed max-w-2xl line-clamp-3">
                {featuredGuide.summary}
              </p>

              <div className="flex flex-wrap items-center gap-4 text-xs font-mono text-slate-500">
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  By {featuredGuide.author}
                </span>
                <span>•</span>
                <span>{featuredGuide.date}</span>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-3">
                <Link 
                  to={`/blog/${featuredGuide.slug}`} 
                  className="px-5 py-3 bg-white hover:bg-slate-100 text-slate-950 text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5"
                >
                  <span>Read Full Guide</span>
                  <ChevronRight className="w-4 h-4 text-slate-950" />
                </Link>
                <Link 
                  to={featuredGuide.relatedCalculatorUrl} 
                  className="px-5 py-3 bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-800 rounded-xl text-xs font-bold transition-colors flex items-center gap-2"
                >
                  <Calculator className="w-4 h-4 text-amber-500" />
                  <span>Go to {featuredGuide.relatedCalculator}</span>
                </Link>
              </div>
            </div>

            <div className="hidden lg:grid lg:col-span-4 p-6.5 rounded-2xl bg-slate-950/50 border border-slate-900 space-y-4">
              <span className="text-[10px] font-mono tracking-wider font-bold text-slate-500 uppercase">
                CALCULATOR INTEGRATION
              </span>
              <h3 className="text-sm font-bold text-slate-200">
                Need to calculate {featuredGuide.category} options?
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed font-light">
                Directly map balances and check results with our high-precision model.
              </p>
              <Link 
                to={featuredGuide.relatedCalculatorUrl}
                className="w-full flex items-center justify-center gap-2 py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-extrabold rounded-xl transition-all"
              >
                <Calculator className="w-3.5 h-3.5" />
                <span>Open Related Tool</span>
              </Link>
            </div>
          </div>
        </article>
      )}

      {/* Main Grid: Articles + Sidebar Context */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 text-left">
        
        {/* Left Side: Dynamic Articles Grid (Filtered/Queried) */}
        <div className="lg:col-span-8 space-y-6">
          {gridGuides.length === 0 ? (
            <div className="bg-slate-950/45 border border-slate-905 rounded-2xl p-12 text-center text-slate-400 space-y-3">
              <Sliders className="w-8 h-8 text-slate-600 mx-auto" />
              <p className="text-sm">No articles matched your search filter criteria.</p>
              <button 
                onClick={() => { setSearchQuery(""); setActiveCategory("all"); }} 
                className="text-xs text-amber-500 font-mono font-bold underline"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {gridGuides.map((guide) => (
                <article 
                  key={guide.slug}
                  className="bg-slate-950/45 hover:bg-slate-950/75 border border-slate-900 hover:border-slate-800 rounded-2xl p-6 transition-all duration-300 flex flex-col justify-between hover:shadow-lg space-y-5 group"
                >
                  <div className="space-y-3.5">
                    {/* Meta details */}
                    <div className="flex items-center justify-between text-[10px] font-mono select-none text-slate-500">
                      <span className="text-amber-550 uppercase tracking-widest font-bold">
                        {guide.category}
                      </span>
                      <span className="flex items-center gap-1 font-semibold text-slate-550">
                        <Clock className="w-3 h-3 text-slate-600" />
                        {guide.readTime}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg sm:text-xl font-bold tracking-tight text-white leading-snug group-hover:text-amber-400 transition-colors">
                      <Link to={`/blog/${guide.slug}`}>
                        {guide.title}
                      </Link>
                    </h3>

                    {/* Excerpt Summary */}
                    <p className="text-slate-400 text-xs sm:text-sm font-light leading-relaxed line-clamp-3">
                      {guide.summary}
                    </p>

                    {/* Footer Meta */}
                    <div className="text-[11px] font-mono text-slate-650 flex items-center justify-between pt-1 border-t border-slate-900/50">
                      <span>{guide.author}</span>
                      <span>{guide.date}</span>
                    </div>
                  </div>

                  {/* Redesigned Card Actions: Purely constraints compliant */}
                  <div className="pt-3 border-t border-slate-900/60 flex flex-col gap-2.5">
                    <Link
                      to={`/blog/${guide.slug}`}
                      className="w-full text-center px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-slate-200 hover:text-white text-xs font-bold rounded-xl transition-all border border-slate-800"
                    >
                      Read Full Guide
                    </Link>

                    <Link
                      to={guide.relatedCalculatorUrl}
                      className="w-full text-center inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-amber-500/10 hover:bg-amber-500/25 text-amber-400 text-xs font-semibold rounded-xl transition-all border border-amber-500/15"
                    >
                      <Calculator className="w-3.5 h-3.5 text-amber-500" />
                      <span>Go to {guide.relatedCalculator}</span>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* Right Side: High Converting Sidebar Widgets */}
        <aside className="lg:col-span-4 space-y-6">
          
          {/* Editorial Quality Standards Stamp */}
          <div className="bg-[#020612]/70 rounded-2xl border border-slate-900 p-6 space-y-4">
            <h4 className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" />
              <span>Editorial Integrity</span>
            </h4>
            <p className="text-xs text-slate-400 leading-relaxed font-light">
              We compile personal finance definitions, mortgage amortization ratios, compound yield timelines, and industry-standard debt reduction procedures. Every resource is vetted strictly for educational calculations and does not constitute certified brokerage or tax advisor solicitation.
            </p>
          </div>

          {/* Quick-Access Tools List */}
          <div className="p-6 bg-slate-950/45 border border-slate-900 rounded-2xl space-y-4">
            <h4 className="text-xs font-mono font-bold uppercase tracking-widest text-slate-400 border-b border-slate-900 pb-2.5 flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5 text-amber-500" />
              <span>Authoritative Calculators</span>
            </h4>
            
            <div className="space-y-3">
              <Link 
                to="/loan-calculator" 
                className="flex items-center justify-between p-3 bg-slate-900/40 hover:bg-slate-900 rounded-xl transition-all text-xs border border-slate-800/60 text-slate-300 hover:text-white"
              >
                <span className="font-medium">Mortgage Loan Amortization</span>
                <ChevronRight className="w-3.5 h-3.5 text-amber-500" />
              </Link>
              <Link 
                to="/compound-interest-calculator" 
                className="flex items-center justify-between p-3 bg-slate-900/40 hover:bg-slate-900 rounded-xl transition-all text-xs border border-slate-800/60 text-slate-300 hover:text-white"
              >
                <span className="font-medium">Compound Interest Evaluator</span>
                <ChevronRight className="w-3.5 h-3.5 text-amber-500" />
              </Link>
              <Link 
                to="/retirement-calculator" 
                className="flex items-center justify-between p-3 bg-slate-900/40 hover:bg-slate-900 rounded-xl transition-all text-xs border border-slate-800/60 text-slate-300 hover:text-white"
              >
                <span className="font-medium">Retirement Safety Planner</span>
                <ChevronRight className="w-3.5 h-3.5 text-amber-500" />
              </Link>
              <Link 
                to="/debt-payoff-calculator" 
                className="flex items-center justify-between p-3 bg-slate-900/40 hover:bg-slate-900 rounded-xl transition-all text-xs border border-slate-800/60 text-slate-300 hover:text-white"
              >
                <span className="font-medium">Debt Payoff Snowball</span>
                <ChevronRight className="w-3.5 h-3.5 text-amber-500" />
              </Link>
            </div>
          </div>

          {/* Luxury Subscription CTA sidebar element */}
          <div className="bg-gradient-to-tr from-slate-950 via-[#0a101f] to-slate-950 border border-blue-900/25 rounded-2xl p-6 space-y-4">
            <span className="text-[9px] font-mono font-bold text-amber-550 uppercase tracking-widest">SUBSCRIBE</span>
            <h3 className="text-sm font-bold text-white tracking-tight">
              Fintech Strategies Delivery
            </h3>
            <p className="text-xs text-slate-400 font-light leading-relaxed">
              Vetted financial modeling, key compound sheets, and expert optimization strategies delivered monthly.
            </p>
            <input 
              type="email" 
              placeholder="Your email address" 
              className="w-full bg-slate-900 border border-slate-800 focus:outline-none focus:border-amber-500/40 text-xs px-3.5 py-2.5 rounded-xl text-slate-300 placeholder-slate-500 font-light"
            />
            <button 
              onClick={() => alert("Subscribed!")} 
              className="w-full py-2.5 bg-white hover:bg-slate-100 text-slate-950 text-xs font-bold rounded-xl transition-colors cursor-pointer"
            >
              Get Updates
            </button>
          </div>

        </aside>

      </div>
    </div>
  );
}
